/**
 * Extended UI Components
 * These are custom implementations that replace Ant Design components
 */

import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Search,
  Calendar,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";
import React, { useRef, useState } from "react";

// Pagination Component
interface PaginationProps {
  current: number;
  total: number;
  pageSize: number;
  onChange: (page: number, pageSize?: number) => void;
  showSizeChanger?: boolean;
  showTotal?: (total: number, range?: [number, number]) => React.ReactNode;
  pageSizeOptions?: string[];
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  current,
  total,
  pageSize,
  onChange,
  showTotal,
  className = "",
}) => {
  const totalPages = Math.ceil(total / pageSize);
  const startPage = Math.max(1, current - 2);
  const endPage = Math.min(totalPages, current + 2);
  const pages = Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i
  );

  return (
    <div className={`flex items-center justify-center gap-2 mt-6 ${className}`}>
      {showTotal && <div className="mr-4">{showTotal(total)}</div>}

      <button
        onClick={() => onChange(current - 1)}
        disabled={current === 1}
        className="p-2 rounded border border-secondary hover:border-accent disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      {startPage > 1 && (
        <>
          <button
            onClick={() => onChange(1)}
            className="px-3 py-1 rounded border border-secondary hover:border-accent"
          >
            1
          </button>
          {startPage > 2 && <span className="px-2">...</span>}
        </>
      )}

      {pages.map((page) => (
        <button
          key={page}
          onClick={() => onChange(page)}
          className={`px-3 py-1 rounded border ${
            page === current
              ? "border-accent bg-accent text-white"
              : "border-secondary hover:border-accent"
          }`}
        >
          {page}
        </button>
      ))}

      {endPage < totalPages && (
        <>
          {endPage < totalPages - 1 && <span className="px-2">...</span>}
          <button
            onClick={() => onChange(totalPages)}
            className="px-3 py-1 rounded border border-secondary hover:border-accent"
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        onClick={() => onChange(current + 1)}
        disabled={current === totalPages}
        className="p-2 rounded border border-secondary hover:border-accent disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};

// Input Components
interface InputProps {
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  allowClear?: boolean;
  prefix?: React.ReactNode;
  style?: React.CSSProperties;
  size?: "small" | "default" | "large" | string;
}

export const Input: React.FC<InputProps> & { Search: React.FC<InputProps> } = ({
  placeholder,
  value,
  defaultValue,
  onChange,
  className = "",
  allowClear,
  prefix,
  style,
}) => {
  const [inputValue, setInputValue] = useState(value || defaultValue || "");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onChange?.(e);
  };

  const handleClear = () => {
    const event = {
      target: { value: "" },
    } as React.ChangeEvent<HTMLInputElement>;
    setInputValue("");
    onChange?.(event);
  };

  return (
    <div className={`relative ${className}`} style={style}>
      {prefix && (
        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary">
          {prefix}
        </span>
      )}
      <input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleChange}
        className={`w-full ${prefix ? "pl-10" : "px-3"} pr-3 py-2 border border-secondary rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-primary text-primary`}
      />
      {allowClear && inputValue && (
        <button
          onClick={handleClear}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-secondary hover:text-primary"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

Input.Search = ({ placeholder, onChange, className = "", allowClear }) => {
  const [inputValue, setInputValue] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onChange?.(e);
  };

  const handleClear = () => {
    const event = {
      target: { value: "" },
    } as React.ChangeEvent<HTMLInputElement>;
    setInputValue("");
    onChange?.(event);
  };

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleChange}
        className="w-full pl-10 pr-3 py-2 border border-secondary rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-primary text-primary"
      />
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary" />
      {allowClear && inputValue && (
        <button
          onClick={handleClear}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-secondary hover:text-primary"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

// Spin Component
interface SpinProps {
  size?: "small" | "default" | "large";
  tip?: string;
  children?: React.ReactNode;
}

export const Spin: React.FC<SpinProps> = ({
  size = "default",
  tip,
  children,
}) => {
  const sizeClasses = {
    small: "h-4 w-4",
    default: "h-6 w-6",
    large: "h-8 w-8",
  };

  const spinner = (
    <div className="flex flex-col items-center justify-center">
      <Loader2 className={`${sizeClasses[size]} animate-spin text-accent`} />
      {tip && <div className="mt-2 text-sm text-secondary">{tip}</div>}
    </div>
  );

  if (children) {
    return (
      <div className="relative">
        <div className="opacity-50">{children}</div>
        <div className="absolute inset-0 flex items-center justify-center bg-primary/50">
          {spinner}
        </div>
      </div>
    );
  }

  return spinner;
};

// Alert Component
interface AlertProps {
  message: string;
  description?: string;
  type?: "success" | "info" | "warning" | "error";
  showIcon?: boolean;
  closable?: boolean;
  onClose?: () => void;
  className?: string;
}

export const Alert: React.FC<AlertProps> = ({
  message,
  description,
  type = "info",
  showIcon = false,
  closable = false,
  onClose,
  className = "",
}) => {
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    onClose?.();
  };

  if (!visible) return null;

  const typeStyles = {
    success: "border-green-500 bg-green-50 text-green-800",
    info: "border-blue-500 bg-blue-50 text-blue-800",
    warning: "border-yellow-500 bg-yellow-50 text-yellow-800",
    error: "border-red-500 bg-red-50 text-red-800",
  };

  const icons = {
    success: <CheckCircle className="h-5 w-5" />,
    info: <AlertCircle className="h-5 w-5" />,
    warning: <AlertTriangle className="h-5 w-5" />,
    error: <AlertCircle className="h-5 w-5" />,
  };

  return (
    <div className={`border-l-4 p-4 ${typeStyles[type]} rounded-r ${className}`}>
      <div className="flex">
        {showIcon && <div className="flex-shrink-0 mr-3">{icons[type]}</div>}
        <div className="flex-1">
          <h3 className="font-medium">{message}</h3>
          {description && <div className="mt-1 text-sm">{description}</div>}
        </div>
        {closable && (
          <button onClick={handleClose} className="flex-shrink-0 ml-3">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

// Select Component
interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  mode?: "multiple";
  placeholder?: string;
  onChange?: (value: string | string[]) => void;
  options?: SelectOption[];
  className?: string;
  value?: string | string[];
  style?: React.CSSProperties;
}

export const Select: React.FC<SelectProps> = ({
  mode,
  placeholder,
  onChange,
  options = [],
  className = "",
  value = mode === "multiple" ? [] : "",
  style,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>(
    Array.isArray(value) ? value : value ? [value] : []
  );

  const handleSelect = (optionValue: string) => {
    if (mode === "multiple") {
      const newValues = selectedValues.includes(optionValue)
        ? selectedValues.filter((v) => v !== optionValue)
        : [...selectedValues, optionValue];

      setSelectedValues(newValues);
      onChange?.(newValues);
    } else {
      setSelectedValues([optionValue]);
      onChange?.(optionValue);
      setIsOpen(false);
    }
  };

  const displayText =
    mode === "multiple"
      ? selectedValues.length
        ? `${selectedValues.length} selected`
        : placeholder || "Select options"
      : selectedValues.length
      ? options.find((opt) => opt.value === selectedValues[0])?.label ||
        selectedValues[0]
      : placeholder || "Select option";

  return (
    <div className={`relative ${className}`} style={style}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-secondary rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-primary text-primary text-left flex items-center justify-between"
      >
        <span className={selectedValues.length ? "" : "text-secondary"}>
          {displayText}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-primary border border-secondary rounded-md shadow-lg max-h-60 overflow-auto">
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className={`px-3 py-2 cursor-pointer hover:bg-secondary ${
                selectedValues.includes(option.value)
                  ? "bg-accent text-white"
                  : "text-primary"
              }`}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// DatePicker Component
interface DatePickerProps {
  onChange?: (dates: [string, string] | null) => void;
  className?: string;
}

const DatePickerComponent: React.FC<DatePickerProps> & {
  RangePicker: React.FC<DatePickerProps>;
} = ({ className = "" }) => {
  const [date, setDate] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDate(e.target.value);
  };

  return (
    <div className={`relative ${className}`}>
      <input
        type="date"
        value={date}
        onChange={handleChange}
        className="w-full px-3 py-2 border border-secondary rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-primary text-primary"
      />
      <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary pointer-events-none" />
    </div>
  );
};

DatePickerComponent.RangePicker = ({ onChange, className = "" }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const start = e.target.value;
    setStartDate(start);
    if (start && endDate) {
      onChange?.([start, endDate]);
    } else if (!start && !endDate) {
      onChange?.(null);
    }
  };

  const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const end = e.target.value;
    setEndDate(end);
    if (startDate && end) {
      onChange?.([startDate, end]);
    } else if (!startDate && !end) {
      onChange?.(null);
    }
  };

  return (
    <div className={`flex gap-2 ${className}`}>
      <div className="relative flex-1">
        <input
          type="date"
          value={startDate}
          onChange={handleStartChange}
          placeholder="Start date"
          className="w-full px-3 py-2 border border-secondary rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-primary text-primary"
        />
      </div>
      <span className="flex items-center text-secondary">~</span>
      <div className="relative flex-1">
        <input
          type="date"
          value={endDate}
          onChange={handleEndChange}
          placeholder="End date"
          className="w-full px-3 py-2 border border-secondary rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-primary text-primary"
        />
      </div>
    </div>
  );
};

export const DatePicker = DatePickerComponent;

// Tag Component
interface TagProps {
  children: React.ReactNode;
  color?: string;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  title?: string;
}

export const Tag: React.FC<TagProps> = ({
  children,
  color,
  className = "",
  onClick,
  title,
}) => {
  const colorClasses = color
    ? `bg-${color}-100 text-${color}-800 border-${color}-200`
    : "bg-secondary text-primary border-secondary";

  return (
    <span
      className={`inline-block px-2 py-1 text-xs rounded border ${colorClasses} ${className}`}
      onClick={onClick}
      title={title}
    >
      {children}
    </span>
  );
};

// Tooltip Component
interface TooltipProps {
  title: React.ReactNode;
  children: React.ReactNode;
  placement?: "top" | "bottom" | "left" | "right";
}

export const Tooltip: React.FC<TooltipProps> = ({
  title,
  children,
  placement = "top",
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const positionClasses = {
    top: "bottom-full mb-2 left-1/2 transform -translate-x-1/2",
    bottom: "top-full mt-2 left-1/2 transform -translate-x-1/2",
    left: "right-full mr-2 top-1/2 transform -translate-y-1/2",
    right: "left-full ml-2 top-1/2 transform -translate-y-1/2",
  };

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-pointer"
      >
        {children}
      </div>
      {isVisible && (
        <div
          className={`absolute z-50 px-3 py-2 text-sm bg-gray-900 text-white rounded-md shadow-lg whitespace-normal max-w-xs ${positionClasses[placement]}`}
        >
          {title}
        </div>
      )}
    </div>
  );
};

// Typography Components
export const Title = ({
  level = 1,
  children,
  className = "",
}: {
  level?: 1 | 2 | 3 | 4 | 5;
  children: React.ReactNode;
  className?: string;
}) => {
  const Tag = `h${level}` as keyof JSX.IntrinsicElements;
  const sizeClasses = {
    1: "text-4xl font-bold",
    2: "text-3xl font-bold",
    3: "text-2xl font-semibold",
    4: "text-xl font-semibold",
    5: "text-lg font-medium",
  };
  return <Tag className={`${sizeClasses[level]} ${className}`}>{children}</Tag>;
};

export const Text = ({
  children,
  strong,
  type,
  className = "",
}: {
  children: React.ReactNode;
  strong?: boolean;
  type?: "secondary";
  className?: string;
}) => {
  const classes = `${strong ? "font-semibold" : ""} ${
    type === "secondary" ? "text-secondary" : "text-primary"
  } ${className}`;
  return <span className={classes}>{children}</span>;
};

export const Paragraph = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <p className={`text-primary ${className}`}>{children}</p>;
};

export const Typography = {
  Title,
  Text,
  Paragraph,
};

// Layout Components
export const Row = ({
  children,
  gutter,
  align,
  className = "",
}: {
  children: React.ReactNode;
  gutter?: [number, number];
  align?: string;
  className?: string;
}) => {
  const gapClass = gutter ? `gap-${Math.floor(gutter[0] / 4)}` : "gap-4";
  const alignClass = align === "middle" ? "items-center" : "";
  return (
    <div className={`flex flex-wrap ${gapClass} ${alignClass} ${className}`}>
      {children}
    </div>
  );
};

export const Col = ({
  children,
  xs,
  sm,
  md,
  lg,
  xl,
  flex,
  className = "",
}: {
  children: React.ReactNode;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  flex?: string | number;
  className?: string;
}) => {
  let classes = "";
  if (flex === "auto" || flex === 1) classes += "flex-1 ";
  else if (flex) classes += `flex-${flex} `;
  if (xs) classes += `w-full `;
  if (sm) classes += `sm:w-${sm}/12 `;
  if (md) classes += `md:w-${md}/12 `;
  if (lg) classes += `lg:w-${lg}/12 `;
  if (xl) classes += `xl:w-${xl}/12 `;
  return <div className={`${classes} ${className}`}>{children}</div>;
};

// Divider Component
export const Divider = ({ className = "" }: { className?: string }) => {
  return <div className={`border-t border-secondary ${className}`} />;
};

// Slider Component
interface SliderProps {
  min?: number;
  max?: number;
  value?: [number, number];
  onChange?: (value: [number, number]) => void;
  className?: string;
}

export const Slider: React.FC<SliderProps> = ({
  min = 0,
  max = 100,
  value = [min, max],
  onChange,
  className = "",
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState<"min" | "max" | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const minPercent = ((localValue[0] - min) / (max - min)) * 100;
  const maxPercent = ((localValue[1] - min) / (max - min)) * 100;

  const getValueFromPosition = (clientX: number) => {
    if (!trackRef.current) return min;

    const rect = trackRef.current.getBoundingClientRect();
    const percentage = Math.max(
      0,
      Math.min(1, (clientX - rect.left) / rect.width)
    );
    return Math.round(min + percentage * (max - min));
  };

  const handleMouseDown = (type: "min" | "max") => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(type);
  };

  const handleMouseMove = React.useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const newValue = getValueFromPosition(e.clientX);

    if (isDragging === "min") {
      const newMin = Math.max(min, Math.min(newValue, localValue[1]));
      const updatedValue: [number, number] = [newMin, localValue[1]];
      setLocalValue(updatedValue);
      onChange?.(updatedValue);
    } else {
      const newMax = Math.min(max, Math.max(newValue, localValue[0]));
      const updatedValue: [number, number] = [localValue[0], newMax];
      setLocalValue(updatedValue);
      onChange?.(updatedValue);
    }
  }, [isDragging, localValue, min, max, onChange]);

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  return (
    <div className={`relative ${className}`}>
      <div
        ref={trackRef}
        className="relative h-1 bg-secondary rounded-full cursor-pointer"
        onClick={(e) => {
          const clickValue = getValueFromPosition(e.clientX);
          const distToMin = Math.abs(clickValue - localValue[0]);
          const distToMax = Math.abs(clickValue - localValue[1]);

          if (distToMin <= distToMax) {
            const newMin = Math.max(min, Math.min(clickValue, localValue[1]));
            const updatedValue: [number, number] = [newMin, localValue[1]];
            setLocalValue(updatedValue);
            onChange?.(updatedValue);
          } else {
            const newMax = Math.min(max, Math.max(clickValue, localValue[0]));
            const updatedValue: [number, number] = [localValue[0], newMax];
            setLocalValue(updatedValue);
            onChange?.(updatedValue);
          }
        }}
      >
        <div
          className="absolute h-1 bg-accent rounded-full pointer-events-none"
          style={{
            left: `${minPercent}%`,
            width: `${maxPercent - minPercent}%`,
          }}
        />
        <div
          className="absolute w-4 h-4 bg-accent border-2 border-white rounded-full shadow-md cursor-grab active:cursor-grabbing transform -translate-y-1.5 -translate-x-2 z-10"
          style={{ left: `${minPercent}%` }}
          onMouseDown={handleMouseDown("min")}
        />
        <div
          className="absolute w-4 h-4 bg-accent border-2 border-white rounded-full shadow-md cursor-grab active:cursor-grabbing transform -translate-y-1.5 -translate-x-2 z-10"
          style={{ left: `${maxPercent}%` }}
          onMouseDown={handleMouseDown("max")}
        />
      </div>
    </div>
  );
};

// Tabs Component
interface TabItem {
  key: string;
  label: string;
  children: React.ReactNode;
}

interface TabsProps {
  items: TabItem[];
  defaultActiveKey?: string;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  items,
  defaultActiveKey,
  className = "",
}) => {
  const [activeKey, setActiveKey] = useState(
    defaultActiveKey || items[0]?.key || ""
  );

  const activeTab = items.find((item) => item.key === activeKey);

  return (
    <div className={className}>
      <div className="flex border-b border-secondary">
        {items.map((item) => (
          <button
            key={item.key}
            onClick={() => setActiveKey(item.key)}
            className={`px-4 py-2 border-b-2 transition-colors ${
              activeKey === item.key
                ? "border-accent text-accent"
                : "border-transparent text-secondary hover:text-primary"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="mt-4">{activeTab?.children}</div>
    </div>
  );
};

// Progress Component
interface ProgressProps {
  percent: number;
  strokeColor?: string;
  trailColor?: string;
  size?: "small" | "default" | "large";
  className?: string;
  showInfo?: boolean;
  strokeWidth?: number;
}

export const Progress: React.FC<ProgressProps> = ({
  percent,
  strokeColor = "rgb(99, 102, 241)",
  trailColor = "rgb(229, 231, 235)",
  size = "default",
  className = "",
  showInfo = true,
  strokeWidth,
}) => {
  const sizeClasses = {
    small: "h-1",
    default: "h-2",
    large: "h-3",
  };

  const heightStyle = strokeWidth ? { height: `${strokeWidth}px` } : undefined;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div
        className={`flex-1 bg-gray-200 rounded-full overflow-hidden ${heightStyle ? "" : sizeClasses[size]}`}
        style={{ backgroundColor: trailColor, ...heightStyle }}
      >
        <div
          className="h-full transition-all duration-300 ease-out rounded-full"
          style={{
            width: `${Math.min(100, Math.max(0, percent))}%`,
            backgroundColor: strokeColor,
          }}
        />
      </div>
      {showInfo && (
        <span className="text-sm text-secondary min-w-[3rem] text-right">
          {Math.round(percent)}%
        </span>
      )}
    </div>
  );
};

// Segmented Component
interface SegmentedOption {
  label: string;
  value: string;
}

interface SegmentedProps {
  options: SegmentedOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export const Segmented: React.FC<SegmentedProps> = ({
  options,
  value,
  onChange,
  className = "",
}) => {
  return (
    <div
      className={`flex rounded-md border border-secondary bg-primary p-1 ${className}`}
    >
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`px-3 py-1 text-sm rounded transition-colors ${
            value === option.value
              ? "bg-accent text-white"
              : "text-primary hover:bg-secondary"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

// Checkbox Component
interface CheckboxProps {
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
  children?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  checked = false,
  indeterminate = false,
  onChange,
  children,
  disabled = false,
  className = "",
  style,
}) => {
  return (
    <label
      className={`flex items-center gap-2 cursor-pointer ${
        disabled ? "opacity-50 cursor-not-allowed" : ""
      } ${className}`}
      style={style}
    >
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange?.(e.target.checked)}
          disabled={disabled}
          className="sr-only"
        />
        <div
          className={`w-4 h-4 border-2 rounded transition-colors ${
            checked || indeterminate
              ? "bg-accent border-accent"
              : "border-secondary bg-primary hover:border-accent/50"
          }`}
        >
          {checked && !indeterminate && (
            <CheckCircle className="w-3 h-3 text-white absolute -top-0.5 -left-0.5" />
          )}
          {indeterminate && (
            <div className="w-2 h-0.5 bg-white absolute top-1.5 left-1" />
          )}
        </div>
      </div>
      {children && (
        <span className={disabled ? "text-secondary" : "text-primary"}>
          {children}
        </span>
      )}
    </label>
  );
};

// Card Component (extended version)
interface CardComponentProps {
  children: React.ReactNode;
  className?: string;
  title?: string;
  hoverable?: boolean;
  onClick?: () => void;
}

export const CardComponent: React.FC<CardComponentProps> = ({
  children,
  className = "",
  title,
  hoverable = false,
  onClick,
}) => {
  const baseClasses = "bg-primary border border-secondary rounded-lg shadow-sm";
  const hoverClasses = hoverable
    ? "hover:shadow-md hover:border-accent/20 cursor-pointer transition-all duration-200"
    : "";

  return (
    <div
      className={`${baseClasses} ${hoverClasses} ${className}`}
      onClick={onClick}
    >
      {title && (
        <div className="px-4 py-3 border-b border-secondary">
          <h3 className="text-lg font-medium text-primary">{title}</h3>
        </div>
      )}
      <div className="p-4">{children}</div>
    </div>
  );
};

// Empty Component
interface EmptyProps {
  description?: string;
  className?: string;
}

export const Empty: React.FC<EmptyProps> = ({
  description = "No data",
  className = "",
}) => {
  return (
    <div
      className={`flex flex-col items-center justify-center py-8 text-secondary ${className}`}
    >
      <div className="w-16 h-16 mb-4 opacity-40">
        <svg viewBox="0 0 64 64" className="w-full h-full">
          <circle
            cx="32"
            cy="32"
            r="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="4,4"
          />
          <path
            d="M20 32h24M32 20v24"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <p className="text-sm">{description}</p>
    </div>
  );
};

// Avatar Component
interface AvatarProps {
  src?: string;
  alt?: string;
  size?: number | "small" | "default" | "large";
  className?: string;
  children?: React.ReactNode;
  shape?: "circle" | "square";
}

export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt = "",
  size = "default",
  className = "",
  children,
  shape = "circle",
}) => {
  const sizeClasses =
    typeof size === "number"
      ? ""
      : size === "small"
      ? "w-8 h-8 text-sm"
      : size === "large"
      ? "w-16 h-16 text-lg"
      : "w-10 h-10";

  const sizeStyles =
    typeof size === "number" ? { width: size, height: size } : {};

  const shapeClass = shape === "square" ? "rounded-md" : "rounded-full";

  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        className={`${shapeClass} object-cover ${sizeClasses} ${className}`}
        style={sizeStyles}
      />
    );
  }

  return (
    <div
      className={`${shapeClass} bg-accent text-white flex items-center justify-center font-medium ${sizeClasses} ${className}`}
      style={sizeStyles}
    >
      {children || alt?.charAt(0)?.toUpperCase() || "?"}
    </div>
  );
};

// Descriptions Component
interface DescriptionsItemProps {
  label: React.ReactNode;
  children: React.ReactNode;
}

interface DescriptionsProps {
  title?: string;
  children: React.ReactNode;
  column?: number;
  className?: string;
  bordered?: boolean;
  size?: "small" | "default" | "large" | string;
}

export const Descriptions: React.FC<DescriptionsProps> & {
  Item: React.FC<DescriptionsItemProps>;
} = ({ title, children, column = 1, className = "", bordered = false }) => {
  return (
    <div className={`${bordered ? "border border-secondary rounded-md p-4" : ""} ${className}`}>
      {title && (
        <h3 className="text-lg font-medium mb-4 text-primary">{title}</h3>
      )}
      <div
        className={`grid gap-3 ${
          column > 1 ? `grid-cols-1 md:grid-cols-${column}` : "grid-cols-1"
        }`}
      >
        {children}
      </div>
    </div>
  );
};

Descriptions.Item = ({ label, children }: DescriptionsItemProps) => {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-4">
      <div className="text-sm font-medium text-secondary min-w-0 sm:w-32 flex-shrink-0">
        {label}:
      </div>
      <div className="text-sm text-primary flex-1">{children}</div>
    </div>
  );
};
